import dayjs from 'dayjs'
import {
  AppliedPtoItem,
  DistributionFormValues,
  ExistingUserApiItem,
  ExistingUserItem,
  PromoCodeApiItem,
  PromoCodeAppliedPtoApiItem,
  PromoCodeCreatePayload,
  PromoCodeDistributionApiItem,
  PromoCodeDistributionItem,
  PromoCodeFormValues,
  PromoCodeItem,
  PromoCodeStatus,
} from 'Modules'

import {
  DISTRIBUTION_EMAIL_STATUS_FROM_API,
  DISTRIBUTION_SEND_MODE_TO_API,
  DISTRIBUTION_TYPE_FROM_API,
  DISTRIBUTION_TYPE_TO_API,
  PROMO_CODE_STATUS_FROM_CODE,
  PROMO_CODE_TYPE_FROM_API,
  PROMO_CODE_TYPE_TO_API,
} from 'constants/promo-code'

const API_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss'

const STATUS_STRING_MAP: Record<string, PromoCodeStatus> = {
  scheduled: 'SCHEDULED',
  active: 'ACTIVE',
  expired: 'EXPIRED',
}

/** BE may send status as a numeric code or a status string — handle both. */
const resolveStatus = (status: number | string): PromoCodeStatus => {
  if (typeof status === 'number') return PROMO_CODE_STATUS_FROM_CODE[status] ?? 'SCHEDULED'
  return STATUS_STRING_MAP[status.toLowerCase()] ?? 'SCHEDULED'
}

export const mapApiToPromoCodeItem = (api: PromoCodeApiItem): PromoCodeItem => {
  // List endpoint sends `typeBonus`, detail endpoint sends `type`.
  const apiType = api.typeBonus ?? api.type
  const type = (apiType && PROMO_CODE_TYPE_FROM_API[apiType]) ?? 'DISCOUNT'
  const params = api.reward?.params
  const percent = params?.percent
  const isBonus = type === 'PTO_BONUS'
  // PTO Bonus is stored as percent + minPtoQuantity (the "Buy" amount);
  // recover "get" = percent * buy / 100.
  const bonusBuy = isBonus ? params?.minPtoQuantity : undefined
  const bonusGet =
    isBonus && bonusBuy != null && percent != null
      ? Math.round((percent * bonusBuy) / 100)
      : undefined
  const usageLimit =
    api.usageLimit === 'one_per_user'
      ? 'ONE_PER_USER'
      : api.usageLimit === 'unlimited'
        ? 'UNLIMITED'
        : undefined

  return {
    id: api._id,
    code: api.codeContent,
    type,
    value: isBonus ? undefined : percent,
    tokenType: params?.tokenType,
    bonusBuy,
    bonusGet,
    ptoSoldOut: api.ptoSoldOutRequired,
    usageLimit,
    createdAt: api.createdAt,
    startDate: api.startAt ?? '',
    endDate: api.endAt ?? '',
    usedTimes: api.usedTimes ?? 0,
    totalSupply:
      api.totalSupply?.type === 'limited' ? (api.totalSupply.limit ?? undefined) : undefined,
    totalPtosApplied: api.totalPtosApplied ?? 0,
    status: resolveStatus(api.status),
    // Accept either populated PTOs or a bare id list — SelectPto resolves the
    // display name from its own fetch, so an id-only placeholder is fine.
    linkedPtos: api.linkedPtos?.length
      ? api.linkedPtos.map((pto) => ({ id: pto._id, name: pto.fullName ?? pto._id }))
      : (api.linkedPtoIds ?? []).map((id) => ({ id, name: id })),
    // null latestDistributionType => whitelist not set up yet ("Set up" cell).
    distribution:
      api.latestDistributionType != null
        ? {
            type: DISTRIBUTION_TYPE_FROM_API[api.latestDistributionType] ?? 'ALL_USERS',
            totalUsers: api.latestDistributionTotalUsers ?? undefined,
            totalEmailsSent: api.totalEmailsSent ?? undefined,
          }
        : undefined,
  }
}

/** Reward percent for the BE: PTO Bonus encodes "Buy X get Y" as (Y / X) * 100. */
const toRewardPercent = (values: PromoCodeFormValues): number => {
  if (values.type !== 'PTO_BONUS') return values.value ?? 0
  const buy = values.bonusBuy ?? 0
  const get = values.bonusGet ?? 0
  return buy > 0 ? (get / buy) * 100 : 0
}

/** Map the create/edit form values to the BE create payload. */
export const mapFormToPromoCodePayload = (values: PromoCodeFormValues): PromoCodeCreatePayload => {
  const isBonus = values.type === 'PTO_BONUS'
  const needsTokenType = values.type === 'CASHBACK_TOKEN' || isBonus
  const params: PromoCodeCreatePayload['reward']['params'] = { percent: toRewardPercent(values) }
  if (needsTokenType) params.tokenType = (values.tokenType ?? 'Mey').toLowerCase()
  if (isBonus) params.minPtoQuantity = values.bonusBuy ?? 0

  return {
    codeContent: values.code.trim(),
    type: PROMO_CODE_TYPE_TO_API[values.type],
    reward: { params },
    ptoSoldOutRequired: needsTokenType ? (values.ptoSoldOut ?? false) : false,
    totalSupply:
      values.totalSupplyType === 'LIMITED'
        ? { type: 'limited', limit: values.totalSupply }
        : { type: 'unlimited' },
    usageLimit: values.usageLimit === 'ONE_PER_USER' ? 'one_per_user' : 'unlimited',
    startAt: values.startDate ? dayjs(values.startDate).format(API_DATE_FORMAT) : '',
    endAt: values.endDate ? dayjs(values.endDate).format(API_DATE_FORMAT) : null,
    linkedPtoIds: values.linkedPtoIds ?? [],
  }
}

/** Map a "PTOs by promo code" API row to the popup item. */
export const mapApiToAppliedPto = (api: PromoCodeAppliedPtoApiItem): AppliedPtoItem => ({
  id: api.ptoId,
  ptoId: api.ptoId,
  name: api.ptoName,
  status: api.status?.toLowerCase() === 'inactive' ? 'INACTIVE' : 'ACTIVE',
})

/** Map a distribution API row to the History tab item. */
export const mapApiToDistribution = (
  api: PromoCodeDistributionApiItem
): PromoCodeDistributionItem => ({
  id: api._id,
  type: DISTRIBUTION_TYPE_FROM_API[api.type] ?? 'ALL_USERS',
  fileName: api.fileName ?? undefined,
  subject: api.subject ?? undefined,
  body: api.body ?? undefined,
  emailStatus: DISTRIBUTION_EMAIL_STATUS_FROM_API[api.status] ?? 'NOT_SENT',
  totalUsers: api.totalUsers ?? 0,
  sentCount: api.sentCount ?? 0,
  failedCount: api.failedCount ?? 0,
  createdAt: api.createdAt,
})

export const mapApiToExistingUser = (api: ExistingUserApiItem): ExistingUserItem => ({
  id: api._id,
  email: api.email,
  walletAddress: api.walletAddress,
})

/**
 * Map the Setup form values to the JSON create payload
 * (ALL_USERS / REFERRED_USERS / EXISTING_USERS — IMPORT_FILE goes multipart).
 */
export const mapFormToDistributionPayload = (
  promoCodeId: string,
  values: DistributionFormValues
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    promoCodeId,
    type: DISTRIBUTION_TYPE_TO_API[values.type],
    sendEmail: values.sendEmail,
  }
  if (values.type === 'EXISTING_USERS') payload.userIds = values.userIds ?? []
  if (values.sendEmail) {
    payload.sendMode = DISTRIBUTION_SEND_MODE_TO_API[values.sendMode ?? 'ALL_WHITELIST']
    payload.subject = values.subject?.trim()
    payload.body = values.body?.trim()
  }
  return payload
}
